import React, { Children, useRef } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import PopupNew from 'components/common/Popup/PopupNew';

import { FIELD_INFO_CLICKED } from 'constants/amplitude/AmplitudeEvents';
import { FIELD_NAME, ICON_TYPE } from 'constants/amplitude/AmplitudeParamName';
import { ERROR, REGULAR } from 'constants/amplitude/AmplitudeParamValues';
import { REQ_ERROR, VALID_ERROR } from 'constants/Constants';
import { ERR_NOT_ALLOWED_PAYMENT_TO_COUNTRY, ERR_NOT_ALLOWED_SWIFT } from 'constants/WellKnownErrorCodes';

import amplitude from 'amplitude-js';

import FieldLongTitlePopup from './FieldLongTitlePopup';
import { DiskButtonContainer, FloatingLabel, HintButton, HintContainer, InputContainer, InputLabel, InputRightElement } from './styles';
import { DiskButton } from '../common/DiskButton/DiskButton';
import { Icon } from '../common/Icon/Icon';
import { Text } from '../common/Text/Text';
import ModalNew from '../utils/ModalNew';

const NewInputWrapper = ({ isDetached = false, ...props }) => {
    const popupRef = useRef(null);
    const modalRef = useRef(null);

    const errorMessage = props.isEmptyValue ? props.reqError : props.regexpErrorMessage ? props.regexpErrorMessage : props.validError;

    const isShowErrorHintButton = (errorCode) => {
        if (props.isLoginInputError || props.errorTextLong) {
            return true;
        } else if (errorCode === ERR_NOT_ALLOWED_PAYMENT_TO_COUNTRY || errorCode === ERR_NOT_ALLOWED_SWIFT) {
            return true;
        } else {
            return false;
        }
    };

    const getComment = (fieldId = '', comment = '', nonRepeatable = '', nonRepeatableFill = '', isProd = true) => {
        if (isProd) {
            return comment || '';
        }

        const meta = `[fieldId=${fieldId}][nonRepeatable=${nonRepeatable === true ? 1 : 0}][nonRepeatableFill=${nonRepeatableFill === true ? 1 : 0}]`;

        return comment ? `${meta} ${comment}` : meta;
    };

    return (
        <>
            <InputContainer
                ref={props.forwardedRef}
                isError={props.isError}
                isFilled={props.isFilled}
                isReadOnly={props.isReadOnly}
                isFocused={props.isFocused}
                isLinedInput={props.isLinedInput}
                isCalculatorInput={props.isCalculatorInput}
            >
                <InputLabel
                    htmlFor={props.name}
                    onClick={(e) => props.setFocus(e)}
                    isLinedInput={props.isLinedInput}
                    isPseudoSelect={props.isPseudoSelect}
                    isSmsInput={props.isSmsInput}
                    isCenteredInput={props.isCenteredInput}
                    isError={props.isError}
                    isCalculatorInput={props.isCalculatorInput}
                    isReadOnly={props.isReadOnly}
                >
                    {props.leftInputComponent}
                    {Children.count(props.children) > 1 ? props.children[0] : props.children}
                    <FloatingLabel
                        isError={props.isError}
                        isFilled={props.isFilled}
                        isReadOnly={props.isReadOnly}
                        isFocused={props.isFocused}
                        isLinedInput={props.isLinedInput}
                        isCaptcha={props.isCaptcha}
                        isCenteredInput={props.isCenteredInput}
                    >
                        {props.labelText}
                    </FloatingLabel>
                </InputLabel>
                {props.hasArrowButton && (
                    <DiskButtonContainer>
                        <DiskButton
                            variant="secondary"
                            iconName="arrow-forward-rounded"
                            iconColor="purple"
                            type="button"
                            raised={false}
                            onClick={props.arrowButtonClick}
                        />
                    </DiskButtonContainer>
                )}
                {props.isPseudoSelect && (
                    <InputRightElement>
                        <Icon name="chevron-down" color={props.isReadOnly ? 'greyRgba28' : 'darkGray'} />
                    </InputRightElement>
                )}
                {props.children &&
                    Children.count(props.children) > 1 &&
                    props.children.map((children, index) => {
                        if (index !== 0) {
                            return children;
                        }
                    })}
            </InputContainer>
            {getComment(props.fieldId, props.comment, props.nonRepeatable, props.nonRepeatableFill, true) && !props.isError && (
                <HintContainer isLinedInput={props.isLinedInput}>
                    <Text variant="div" color="mediumGray" size="secondary">
                        {getComment(props.fieldId, props.comment, props.nonRepeatable, props.nonRepeatableFill, true)}
                        {props.commentLong && (
                            <HintButton>
                                <DiskButton
                                    size="small"
                                    type="button"
                                    variant="tertiary"
                                    iconName="info-rounded"
                                    iconColor="mediumGray"
                                    raised={false}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        amplitude.getInstance().logEvent(FIELD_INFO_CLICKED, { [FIELD_NAME]: props.name, [ICON_TYPE]: REGULAR });
                                        popupRef.current?.open(<FieldLongTitlePopup info={props.commentLong} popupRef={popupRef} />);
                                    }}
                                />
                            </HintButton>
                        )}
                    </Text>
                </HintContainer>
            )}
            {props.isError && !isDetached && (
                <HintContainer isLinedInput={props.isLinedInput}>
                    <Text variant="div" color="red" size="secondary">
                        {props.errorMessage || errorMessage}
                        {isShowErrorHintButton(props.errorCode) && (
                            <HintButton>
                                <DiskButton
                                    size="small"
                                    type="button"
                                    variant="errorHint"
                                    iconName="info-rounded"
                                    iconColor="red"
                                    raised={false}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        if (props.errorTextLong) {
                                            amplitude.getInstance().logEvent(FIELD_INFO_CLICKED, { [FIELD_NAME]: props.name, [ICON_TYPE]: ERROR });
                                            popupRef.current?.open(
                                                <FieldLongTitlePopup
                                                    info={props.errorTextLong}
                                                    popupRef={popupRef}
                                                    modalRef={modalRef}
                                                    isError={props.isError}
                                                    field={props.field}
                                                    errorMessage={props.errorMessage || errorMessage}
                                                    reportButtonClick={props.reportButtonClick}
                                                />,
                                            );
                                        } else if (props.hintButtonClick) {
                                            props.hintButtonClick(props.errorCode, props.name);
                                        }
                                    }}
                                />
                            </HintButton>
                        )}
                    </Text>
                </HintContainer>
            )}
            <PopupNew ref={popupRef} />
            <ModalNew ref={modalRef} />
        </>
    );
};

NewInputWrapper.propTypes = {
    errorMessage: PropTypes.string,
    name: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
    labelText: PropTypes.string,
    isFilled: PropTypes.bool,
    isError: PropTypes.bool,
    hasArrowButton: PropTypes.bool,
    isFocused: PropTypes.bool,
    leftInputComponent: PropTypes.object,
    arrowButtonClick: PropTypes.func,
    isLinedInput: PropTypes.bool,
    isPseudoSelect: PropTypes.bool,
    isCaptcha: PropTypes.bool,
    isSmsInput: PropTypes.bool,
    isCenteredInput: PropTypes.bool,
    hintButtonClick: PropTypes.func,
    errorTextLong: PropTypes.string,
    comment: PropTypes.string,
    commentLong: PropTypes.string,
    isInfoButton: PropTypes.bool,
};

const mapStateToProps = (state) => {
    return {
        validError: state.messages.jsMessages[VALID_ERROR],
        reqError: state.messages.jsMessages[REQ_ERROR],
    };
};

export default connect(mapStateToProps)(NewInputWrapper);
